import React, { useEffect, useMemo, useRef, useState } from "react";
import LexicalEditor from "../../../../common/components/text-editor/text-editor";
import {
  DescriptionInputComponent,
  SelectInputComponent,
  TextInputComponent,
} from "../../../../common/components";
import { Box, IconButton, Typography } from "@mui/material";
import { templateStyleOptions } from "../../email-letter-helper";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import "./new-email-template-editor.scss";
import {
  createNewEmailTemplate,
  editEmailTemplates,
  getEmailTemplateDetails,
  getFoldersList,
} from "../../../../api/email-letter-templates";
import {
  replaceMergeFields,
  convertKeysToPascalCase,
} from "../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/email-letter-helper";
import { createLetter, editLetter } from "../../../../api/campaign";
import MODULES from "../../../../constants/modules";
import CloseIcon from "@mui/icons-material/Close";
import { getIconBasedOnFileExt } from "../../../../utils/fetchIconForExtension";
import { AntSwitch } from "../../../user-management/user-management-list/components/ant-switch";
import { routes } from "../../../../constants/routes";
import MoveTemplateToFolderPopup from "../../email-letter-mass-actions/move-to-folder-popup";
import { isLexicalEditorEmpty } from "../../../leads/lead-view/lead-view-details/lead-view-details-view/lead-view-details-view-email-info/email-letter-helper";
import { validateMergeTokens } from "../../email-letter-helper";
import SignaturePopup from "../../../../common/components/create-signature/create-signature-popup";
import { getLetters } from "../../../../api/campaign";

interface NewEmailTemplateEditorProps {
  onClose: () => void;
  setAlertMessage: (message: any) => void;
  toggleEditor?: {
    isOpen: boolean;
    mode: "view" | "edit" | "new";
    template: any;
  };
  setToggleEditor?: any;
  setIsLoading: any;
  templateType?: string;
  doReplaceMergeFields?: boolean;
  personDetails?: any;
  fieldName?: string;
  handleLetterSaveSuccess?: () => void;
  crumbMappingOption?: any;
  setCrumbMappingOption?: (option: any) => void;
  setIsLetterPrintPreviewOpen?: (isOpen: boolean) => void;
  setLetterTemplatePdfUrl?: React.Dispatch<React.SetStateAction<string>>;
  setViewOpen?: React.Dispatch<React.SetStateAction<any>>;
  fetchFoldersList?: () => void;
}

const labelSwitch = { inputProps: { "aria-label": "Switch demo" } };

const emailTemplateSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .max(150, "Max 150 characters"),
  subject: Yup.string().required('Subject is required').max(150, "Max 150 characters"),
});

const letterTemplateSchema = Yup.object({
  name: Yup.string()
    .required("Name is required")
    .max(150, "Max 150 characters"),
  subject: Yup.string().nullable().max(150, "Max 150 characters"),
});

const NewEmailTemplateEditor: React.FC<NewEmailTemplateEditorProps> = ({
  onClose,
  setAlertMessage,
  toggleEditor = { isOpen: false, mode: "new", template: null },
  setToggleEditor,
  setIsLoading,
  templateType = "Email",
  doReplaceMergeFields = false,
  personDetails,
  fieldName,
  handleLetterSaveSuccess,
  crumbMappingOption,
  setCrumbMappingOption,
  setIsLetterPrintPreviewOpen,
  setLetterTemplatePdfUrl,
  setViewOpen
}) => {
  const [emailData, setEmailData] = React.useState<any>({});
  const [placeHolderValues, setPlaceHolderValues] = useState<any>(null);
  const [defaulHtmlMessage, setDefaultHtmlMessage] = useState("");
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const [deletedAttachments, setDeletedAttachments] = React.useState<any[]>([]);
  const [includeOptOutText, setIncludeOptOutText] =
    React.useState<boolean>(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [addFolderOpen, setAddFolderOpen] = useState<boolean>(false);
  const [folderOptions, setFolderOptions] = useState<any[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<any>(null);
  const [createPayload, setCreatePayload] = useState<any>(null);
  const [isPrintPreview, setIsPrintPreview] = useState(false);
  const [templateBodyError, setTemplateBodyError] = useState("");
  const [editorInstance, setEditorInstance] = useState<any>(null);
  const [invalidMergeTokensError, setInvalidMergeTokensError] = useState("");
  const [isSignaturePopupOpen, setIsSignaturePopupOpen] = useState(false);

  const fetchTemplateDetails = async () => {
    try {
      setIsLoading(true);
      const params = {
        templateType: templateType,
        includeBinaryUrls: false,
      }
      const response = await getEmailTemplateDetails(
        toggleEditor?.template?.id,
        params
      );
      if (response?.data?.success === true) {
        setToggleEditor?.((prev: any) => ({
          ...prev,
          template: response?.data?.data,
        }));
        if (response?.data?.data?.attachments?.length > 0) {
          setAttachments(
            response?.data?.data?.attachments.map((file: any) => ({
              name: file?.filename,
              type: file?.mimeType,
              id: file?.id,
              url: file?.file_url,
            }))
          );
        }
        setIncludeOptOutText(response?.data?.data?.include_opt_out_text ?? true);
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };

  const fetchLeadCustomerLetterDetails = async () => {
    try {
      setIsLoading(true);
      let params;
      if (fieldName === MODULES.LEAD_DETAILS) {
        params = {
          leadId: personDetails?.id,
        }
      }
      if (fieldName === MODULES.CUSTOMER_PROFILE) {
        params = {
          customerId: personDetails?.id,
        }
      }
      const response = await getLetters(params,
        fieldName === MODULES.LEAD_DETAILS ? MODULES.LEAD_DETAILS : MODULES.CUSTOMER_PROFILE,
        toggleEditor?.template?.letterId);
      if (response?.data?.success === true) {
        setToggleEditor?.((prev: any) => ({
          ...prev,
          template: { ...response?.data?.data, letterId: response?.data?.data?.id },
        }));
      }
      setIsLoading(false);
    } catch (error: any) {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (toggleEditor?.template?.id && !toggleEditor?.template?.letterId) {
      fetchTemplateDetails();
    }
    if (toggleEditor?.template?.letterId && (fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE)) {
      fetchLeadCustomerLetterDetails();
    }
  }, [toggleEditor?.template?.id, templateType]);

  const handleSave = async (data: any, folder?: any) => {
    try {
      setIsLoading(true);
      const response = await createNewEmailTemplate(data);
      setAlertMessage({
        open: true,
        message: `${templateType} template created and saved to ${folder?.folder_name || '(Not in a folder)'} successfully.`,
        type: "success",
      });

      setIsLoading(false);
      onClose();
      setLetterTemplatePdfUrl?.(response?.data?.data?.preview_url_s3 || toggleEditor?.template?.preview_url_s3 || "");
      return response?.data?.data;
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Error saving email template.",
        type: "error",
      });
      console.error("Error saving email template:", error);
    }
  };

  const handleEditSave = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await editEmailTemplates(
        toggleEditor?.template?.id,
        data
      );
      setAlertMessage({
        open: true,
        message: `${templateType} template updated successfully!`,
        type: "success",
      });
      setIsLoading(false);
      onClose();
      setLetterTemplatePdfUrl?.(response?.data?.data?.preview_url_s3 || toggleEditor?.template?.preview_url_s3 || "");
      return response?.data?.data;
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Error saving email template.",
        type: "error",
      });
      console.error("Error saving email template:", error);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  const handleSaveAndPrintPreview = async (values: any) => {
    const payload = {
      ...values,
      content:
        values.contentType === "HTML" ? emailData.content : values.content,
      contentType: values.contentType === "HTML" ? "HTML" : "Text",
      folderId: toggleEditor?.template?.folder?.id || null,
    };

    try {

      let savedTemplate = toggleEditor.template || null;

      if (toggleEditor.mode === "edit" && !(fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE)) {
        savedTemplate = await handleEditSave(payload);
      }
      else if (fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE) {
        if (templateBodyError) return;
        const payloadForLetter: any = {
          name: payload.name,
          subject: payload.subject,
          body: payload.content,
        }
        if (fieldName === MODULES.LEAD_DETAILS) payloadForLetter.relatedToLeadId = personDetails?.id;
        if (fieldName === MODULES.CUSTOMER_PROFILE) payloadForLetter.relatedToCustomerId = personDetails?.id;
        if (toggleEditor?.template?.letterId) {
          await handleEditLetterForLeadOrCustomer(payloadForLetter);
        }
        else {
          await handleCreateLetterForLeadOrCustomer(payloadForLetter);
        }

      }
      else {
        savedTemplate = await handleSave(payload);
      }

      if (crumbMappingOption && setCrumbMappingOption) {
        const templateName =
          savedTemplate?.name || values.name || toggleEditor?.template?.name;

        const updatedBreadcrumb = [
          {
            label: `${templateType} Templates`,
            link:
              templateType === "Email"
                ? routes.EMAILTEMPLATES
                : routes.LETTERTEMPLATES,
            handleClick: (event: any) => {
              event.preventDefault();
              setViewOpen?.({ isOpen: false, mode: "view", template: null });
              setIsLetterPrintPreviewOpen?.(false);
              setCrumbMappingOption?.([
                {
                  label: `${templateType} Templates`,
                  link:
                    templateType === "Email"
                      ? routes.EMAILTEMPLATES
                      : routes.LETTERTEMPLATES,
                  handleClick: (event: any) => {
                    event.preventDefault();
                  },
                },
              ]);
            },
          },
          {
            label: templateName,
            link: "",
            handleClick: (event: any) => {
              event.preventDefault();
              setIsLetterPrintPreviewOpen?.(false);
              setViewOpen?.({
                isOpen: true,
                mode: "edit",
                template: savedTemplate,
              });
              setCrumbMappingOption?.([
                {
                  label: `${templateType} Templates`,
                  link:
                    templateType === "Email"
                      ? routes.EMAILTEMPLATES
                      : routes.LETTERTEMPLATES,
                  handleClick: (event: any) => {
                    event.preventDefault();
                    setIsLetterPrintPreviewOpen?.(false);
                    setViewOpen?.({ isOpen: false, mode: "view", template: null });
                    setCrumbMappingOption?.([
                      {
                        label: `${templateType} Templates`,
                        link:
                          templateType === "Email"
                            ? routes.EMAILTEMPLATES
                            : routes.LETTERTEMPLATES,
                        handleClick: (event: any) => {
                          event.preventDefault();
                        },
                      },
                    ]);
                  },
                },
                {
                  label: templateName,
                  link: "",
                  handleClick: (event: any) => {
                    event.preventDefault();
                  },
                },
              ]);
            },
          },
          {
            label: "Print Preview",
            link: "",
            handleClick: (event: any) => {
              event.preventDefault();
            },
          },
        ];

        setCrumbMappingOption(updatedBreadcrumb);
      }
      setIsLetterPrintPreviewOpen?.(true);
    } catch (error) {
      console.error("Error in save and print preview:", error);
    }
  };

  const handleRemoveAttachment = (file: any, index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
    if (file?.id) {
      const deletedIds: Set<string | number> = new Set([
        ...deletedAttachments,
        file?.id,
      ]);
      setDeletedAttachments(Array.from(deletedIds));
    }
  };

  const handleSubmitTemplate = (values: any) => {
    const payload = {
      ...values,
      content:
        values.contentType === "HTML" ? emailData.content : values.content,
      contentType: values.contentType === "HTML" ? "HTML" : "Text",
      folderId: toggleEditor?.template?.folder?.id || null,
    };
    const formData = new FormData();
    formData.append("name", payload.name);
    formData.append("subject", payload.subject);
    formData.append("contentType", payload.contentType);
    formData.append("content", payload.content);
    formData.append("templateType", templateType);
    formData.append("includeOptOut", payload.includeOptOut);
    formData.append("folderId", payload.folderId || null);
    if (deletedAttachments?.length > 0) {
      deletedAttachments?.forEach((id) => {
        formData.append("attachmentsIdsToDelete[]", id);
      });
      payload.attachmentsIdsToDelete = deletedAttachments;
    }
    const filterExistingFiles = attachments?.length
      ? attachments.filter((file: any) => !file?.id)
      : [];
    if (filterExistingFiles?.length > 0 && templateType === "Email") {
      filterExistingFiles.forEach((file) => {
        formData.append("attachments", file);
      });
    }
    const activePayload = filterExistingFiles?.length > 0 ? formData : payload;
    if (
      toggleEditor.mode === "edit" &&
      !(
        fieldName === MODULES.LEAD_DETAILS ||
        fieldName === MODULES.CUSTOMER_PROFILE
      )
    ) {
      handleEditSave(activePayload);
      return;
    }
    if (
      fieldName === MODULES.LEAD_DETAILS ||
      fieldName === MODULES.CUSTOMER_PROFILE
    ) {
      if (templateBodyError) return;
      const payloadForLetter:any = {
        name: payload.name,
        subject: payload.subject,
        body: payload.content,
      }
      if (fieldName === MODULES.LEAD_DETAILS) payloadForLetter.relatedToLeadId = personDetails?.id;
      if (fieldName === MODULES.CUSTOMER_PROFILE) payloadForLetter.relatedToCustomerId = personDetails?.id;
      if (toggleEditor?.template?.letterId) {
        handleEditLetterForLeadOrCustomer(payloadForLetter);
      }
      else {
        handleCreateLetterForLeadOrCustomer(payloadForLetter);
      }

      return
    }
    setAddFolderOpen(true);
    setCreatePayload(activePayload);
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files) return;

    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
      "image/png",
      "image/jpeg",
      "text/plain", // .txt
      "text/csv", // .csv
      "application/msword", // .doc
    ];

    const validFiles = Array.from(files).filter((file) =>
      allowedTypes.includes(file.type)
    );

    try {
      const allAttachments = [...attachments, ...validFiles];
      const totalSize = allAttachments.reduce(
        (sum, file) => sum + ((file as any)?.size || 0),
        0
      );

      if (totalSize > 10 * 1024 * 1024) {
        setAlertMessage({
          open: true,
          message: "Total attachments size cannot exceed 10MB.",
          type: "error",
        });

        event.target.value = "";
        return;
      }

      if (allAttachments.length > 5) {
        setAlertMessage({
          open: true,
          message: "You can attach a maximum of 5 files.",
          type: "error",
        });

        event.target.value = "";
        return;
      }

      setAttachments(allAttachments);
    } catch (err) {
      console.error("Error reading files:", err);
      setAlertMessage({
        open: true,
        message: "Failed to upload files. Please try again.",
        type: "error",
      });
    }

    event.target.value = "";
  };

  useEffect(() => {
    console.log("toggleEditor changed:", toggleEditor);
  }, [toggleEditor]);

  useEffect(() => {
    if (personDetails) {
      const result = convertKeysToPascalCase(personDetails);
      setPlaceHolderValues(result);
    }
  }, [personDetails]);

  useEffect(() => {
    if (toggleEditor?.template?.id) {
      if (personDetails && doReplaceMergeFields) {
        const finalHtml = replaceMergeFields(
          toggleEditor?.template?.body,
          placeHolderValues
        );
        setEmailData((prev: any) => ({
          ...prev,
          content: finalHtml,
        }));
        setDefaultHtmlMessage(finalHtml);
      }
    }
  }, [
    personDetails,
    toggleEditor?.template,
    doReplaceMergeFields,
    placeHolderValues,
  ]);

  const handleCreateLetterForLeadOrCustomer = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await createLetter(data, fieldName);
      if( response?.data?.success === true) {
        handleLetterSaveSuccess?.();
        setAlertMessage({
          open: true,
          message: "Letter created successfully!",
          type: "success",
        });
      }
      setIsLoading(false);
      setLetterTemplatePdfUrl?.(response?.data?.data?.data?.printFileUrl || toggleEditor?.template?.printFileUrl || "");
      onClose();
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Error creating letter.",
        type: "error",
      });
      console.error("Error creating letter:", error);
    }
  };

  const handleEditLetterForLeadOrCustomer = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await editLetter(toggleEditor?.template?.letterId, data, fieldName);
      if (response?.data?.success === true) {
        setAlertMessage({
          open: true,
          message: "Letter updated successfully!",
          type: "success",
        });
        handleLetterSaveSuccess?.();
      }
      setIsLoading(false);
      setLetterTemplatePdfUrl?.(response?.data?.data?.data?.printFileUrl || toggleEditor?.template?.printFileUrl || "");
      onClose();
    } catch (error: any) {
      setIsLoading(false);
      setAlertMessage({
        open: true,
        message:
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Error updating letter.",
        type: "error",
      });
      console.error("Error updating letter:", error);
    }
  };

  const handleTemplateMove = (folder: any) => {
    if (!folder || !createPayload) {
      setAlertMessage({
        open: true,
        message: "Please select a folder to move the template.",
        type: "error",
      });
      return;
    }

    let payloadToSend;

    if (createPayload && createPayload instanceof FormData) {
      // Clone existing FormData into a new one
      const fd = new FormData();
      Array.from(createPayload.entries()).forEach(([key, value]) => {
        fd.append(key, value);
      });

      // Append/override folderId
      fd.set(
        "folderId",
        folder?.folder_name === "(Not in a folder)" ? null : folder.id
      );
      payloadToSend = fd;
     
    } else {
      // Plain object case without attachments 
      payloadToSend = {
        ...createPayload,
        folderId:
          folder?.folder_name === "(Not in a folder)" ? null : folder.id,
      };
    }
    setSelectedFolder(folder);
    handleSave(payloadToSend, folder);
  };

  const fetchFoldersList = async () => {
      try {
        const response = await getFoldersList({ templateType: templateType });
        if (response?.data?.success === true) {
          const folders = response?.data?.data?.map((folder: any) => ({
            id: folder.id,
            folder_name: folder.folder_name,
          }));
          setFolderOptions([
            { id: "notInFolder", folder_name: "(Not in a folder)" },
            ...folders,
          ]);
        }
      } catch (error: any) {
        console.error("Error fetching folders:", error);
  
        setAlertMessage({
          open: true,
          message: error?.response?.data?.message || "Failed to fetch folders.",
          type: "error",
        });
      }
  };

  useEffect(() => {
    if(toggleEditor?.mode === 'new') {
      fetchFoldersList();
    }
    setCreatePayload(null);
    setSelectedFolder(null);
  },[toggleEditor?.mode]);

  const callValidateMergeTokens = (htmlString: string) => {
    setInvalidMergeTokensError('');
    const isValidBody = validateMergeTokens(htmlString);
    if (!isValidBody) {
      setInvalidMergeTokensError(
        `${(fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE) ? 'Letter' : 'Template'} contains invalid or incomplete merge tokens`
      );
    }
    return isValidBody;
  };


  return (
    <div className="new-email-template-editor">
      <div className="header-tab">
        <Formik
          enableReinitialize
          initialValues={{
            name: toggleEditor?.template?.name || "",
            subject: toggleEditor?.template?.subject || "",
            contentType:
              toggleEditor?.template?.content_type === "Text"
                ? "Plain Text"
                : "HTML",
            templateType: templateType || "Email",
            content:
              toggleEditor?.template?.content_type === "Text"
                ? toggleEditor?.template?.body
                : "",
            includeOptOut: includeOptOutText ?? true,
          }}
          validationSchema={templateType === "Email" ? emailTemplateSchema : letterTemplateSchema}
          onSubmit={(values) => {
            let validHtmlBody = true;
            if (values?.contentType === "HTML") {
              const editorState = editorInstance?.getEditorState();
              const empty = editorState ? isLexicalEditorEmpty(editorState) : false;
              if (!empty) {
                validHtmlBody = callValidateMergeTokens(emailData?.content);
              }
            }
            if(!invalidMergeTokensError && validHtmlBody) {
              if (isPrintPreview) {
                handleSaveAndPrintPreview(values);
              } else {
                handleSubmitTemplate(values);
              }
            }
          }}
          validateOnBlur={true}
        >
          {({ handleSubmit, handleChange, values, setFieldValue, errors,
            touched,
            setFieldTouched, }) => {
            return (
              <Form>
                <div className="form-group">
                  <TextInputComponent
                    fieldName="name"
                    label="Name"
                    placeholder="Enter Name"
                    value={values?.name || ""}
                    onChange={handleChange}
                    maxlength="150"
                    type="text"
                    optional
                  />
                </div>
                <div className="form-group">
                  <TextInputComponent
                    fieldName="subject"
                    label={
                      templateType === "Letter" ? "Description" : "Subject Line"
                    }
                    placeholder="Enter subject line"
                    value={values?.subject || ""}
                    onChange={handleChange}
                    maxlength="150"
                    type="text"
                    optional={templateType !== "Letter"}
                  />
                </div>
                {
                  templateType === "Letter" &&
                  <button type='button'
                      className="import-btn"
                      onClick={() => setIsSignaturePopupOpen(true)}
                  >
                    Create Signature
                  </button>
                }
                {templateType === "Email" && (
                  <Box className="html-switch-wrapper">
                    <Box className="paid-plans-switch">
                      <AntSwitch
                        {...labelSwitch}
                        checked={values?.includeOptOut || false}
                        onChange={(e) => {
                          setFieldValue("includeOptOut", e.target.checked);
                        }}
                      />
                      <Typography className="paid-plans-status">
                        Include Opt Out Link
                      </Typography>
                    </Box>
                    <div className="template-style-select-wrapper">
                      <SelectInputComponent
                        options={templateStyleOptions || []}
                        onChange={(value: any) => {
                          setFieldValue("contentType", value);
                          if (value === "HTML") {
                            setFieldValue(
                              "content",
                              toggleEditor?.template?.body || ""
                            );
                          } else {
                            setFieldValue("content", "");
                          }
                        }}
                        value={values?.contentType || "HTML"}
                      />
                    </div>
                  </Box>
                )}{" "}
                <>
                  {invalidMergeTokensError && <div className="template-body-validation-error-message">{invalidMergeTokensError}</div>}
                {values?.contentType === "HTML" ? (
                  <LexicalEditor
                    onEditorInit={(editorInstance) => {
                      setEditorInstance(editorInstance);
                    }}
                    handleDataChange={(html: any, editorState: any) => {
                      setTemplateBodyError("");
                      setInvalidMergeTokensError("")
                      setEmailData((prev: any) => ({
                        ...prev,
                        content: html,
                      }));
                    }}
                    defaultHtmlJson={
                      doReplaceMergeFields
                        ? defaulHtmlMessage
                        : toggleEditor?.template?.body
                    }
                    placeHolderValues={placeHolderValues}
                    fieldName={templateType === "Letter" ? 'Letter' : ''}
                    replaceMergeFields={doReplaceMergeFields}
                  />
                ) : (
                  <>
                    <DescriptionInputComponent
                      fieldName="content"
                      label="Content"
                      placeholder="Enter content"
                      value={values?.content || ""}
                      onChange={handleChange}
                      optional={false}
                    />
                  </>
                  )}
                  <div className="error-message">
                    {templateBodyError}
                  </div>
                </>
                {attachments.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 1,
                      maxHeight: 120,
                      overflowY: "auto",
                    }}
                  >
                    {attachments.map((file, index) => {
                      const dotIndex = file?.name?.lastIndexOf(".");
                      const name =
                        dotIndex !== -1
                          ? file?.name?.substring(0, dotIndex)
                          : file?.name;
                      const extension =
                        dotIndex !== -1 ? file?.name?.substring(dotIndex) : "";

                      return (
                        <Box
                          key={index}
                          sx={{
                            width: 120,
                            bgcolor: "#f5f5f5",
                            borderRadius: 1,
                            p: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                              overflow: "hidden",
                              flex: 1,
                            }}
                          >
                            {getIconBasedOnFileExt(
                              file.name.split(".").pop()?.toLowerCase() || ""
                            )}
                            <Typography
                              variant="caption"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                              }}
                              title={file.name}
                            >
                              <Box
                                component="span"
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 80, // Adjust to fit your layout
                                  display: "inline-block",
                                }}
                              >
                                {name}
                              </Box>
                              <Box component="span" sx={{ flexShrink: 0 }}>
                                {extension}
                              </Box>
                            </Typography>
                          </Box>

                          <IconButton
                            size="small"
                            onClick={() => handleRemoveAttachment(file, index)}
                            sx={{ p: "2px" }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      );
                    })}
                  </Box>
                )}
                <Box className="editor-actions">
                  <button
                    type="button"
                    className="cancel-button"
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <Box
                    gap={1}
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    {templateType === "Letter" && (
                      <button
                        className="cancel-button"
                        type="button"
                        onClick={() => {
                          setIsPrintPreview(true);
                          if (fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE) {
                            const editorState = editorInstance?.getEditorState();
                            const empty = editorState ? isLexicalEditorEmpty(editorState) : false;
                            if (empty) {
                              setTemplateBodyError("Letter body cannot be empty.");
                            } else {
                              setTemplateBodyError("");
                            }
                          }
                          handleSubmit(); // triggers Formik validation + submit
                        }}
                      >
                        Save & Print Preview
                      </button>
                    )}
                    {templateType === "Email" && (
                      <>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.txt"
                          multiple
                          ref={fileInputRef}
                          style={{ display: "none" }}
                          onChange={handleFileChange}
                        />

                        <button
                          className="cancel-button"
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          Attach File
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setIsPrintPreview(false);
                        if (fieldName === MODULES.LEAD_DETAILS || fieldName === MODULES.CUSTOMER_PROFILE) {
                          const editorState = editorInstance?.getEditorState();
                          const empty = isLexicalEditorEmpty(editorState);
                          if (empty) {
                            setTemplateBodyError("Letter body cannot be empty.");
                          }else {
                            setTemplateBodyError("");
                          }
                        }
                        handleSubmit();
                      }}
                      className="cancel-button next-button-wrapper"
                    >
                      Save
                    </button>
                  </Box>
                </Box>
              </Form>
            );
          }}
        </Formik>
        <MoveTemplateToFolderPopup
          isOpen={addFolderOpen}
          onClose={() => {
            setAddFolderOpen(false)
            setSelectedFolder(null);
          }}
          onMove={(folder) => {
            setAddFolderOpen(false);
            handleTemplateMove(folder);
          }}
          folders={folderOptions}
          templateType={templateType}
          fetchFoldersList={fetchFoldersList}
        />
        <SignaturePopup
          open={isSignaturePopupOpen}
          onClose={() => { setIsSignaturePopupOpen(false) }}
        />
      </div>
    </div>
  );
};

export default NewEmailTemplateEditor;
